import { z } from 'zod';

export const DISCIPLINES = [
  'STRUCTURED_CABLING',
  'CCTV',
  'ACCESS_CONTROL',
  'FIRE_ALARM',
  'INTRUSION_DETECTION',
  'AUDIO_VISUAL',
  'DAS',
] as const;
export const DisciplineSchema = z.enum(DISCIPLINES);
export type Discipline = z.infer<typeof DisciplineSchema>;

export const USER_ROLES = [
  'ESTIMATOR',
  'SENIOR_ESTIMATOR',
  'PROJECT_MANAGER',
  'EXECUTIVE_VIEWER',
  'ADMIN',
] as const;
export const UserRoleSchema = z.enum(USER_ROLES);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const USER_ROLE_LEVEL: Record<UserRole, number> = {
  ESTIMATOR: 1,
  EXECUTIVE_VIEWER: 2,
  PROJECT_MANAGER: 3,
  SENIOR_ESTIMATOR: 4,
  ADMIN: 5,
};

// Human-readable labels. Frank's mental model is the 3-tier Admin / Lead /
// Estimator system; the DB enum is wider so PROJECT_MANAGER + EXECUTIVE_VIEWER
// remain available as extra tiers.
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  ADMIN: 'Admin',
  SENIOR_ESTIMATOR: 'Lead Estimator',
  PROJECT_MANAGER: 'Project Manager',
  EXECUTIVE_VIEWER: 'Executive Viewer',
  ESTIMATOR: 'Estimator',
};

// Roles that see every project in the company by default and can manage
// project members on any project. Everyone else is gated by project_members.
export const PRIVILEGED_ROLES: readonly UserRole[] = ['ADMIN', 'SENIOR_ESTIMATOR'];
export function isPrivilegedRole(role: UserRole): boolean {
  return PRIVILEGED_ROLES.includes(role);
}

// Per-project membership role.
export const PROJECT_MEMBER_ROLES = ['OWNER', 'COLLABORATOR', 'VIEWER'] as const;
export const ProjectMemberRoleSchema = z.enum(PROJECT_MEMBER_ROLES);
export type ProjectMemberRole = z.infer<typeof ProjectMemberRoleSchema>;

// Per-team role (LEAD users can manage that team's membership).
export const TEAM_MEMBER_ROLES = ['LEAD', 'MEMBER'] as const;
export const TeamMemberRoleSchema = z.enum(TEAM_MEMBER_ROLES);
export type TeamMemberRole = z.infer<typeof TeamMemberRoleSchema>;

export const PRICING_TIERS = ['BUDGET', 'MID', 'PREMIUM'] as const;
export const PricingTierSchema = z.enum(PRICING_TIERS);
export type PricingTier = z.infer<typeof PricingTierSchema>;

export const PROJECT_STATUSES = [
  'DRAFT', 'UPLOADING', 'PROCESSING',
  'AUTOPILOT_RUNNING', 'READY_FOR_REVIEW',
  'IN_REVIEW', 'FINALIZED', 'SUBMITTED', 'AWARDED', 'LOST',
] as const;
export const ProjectStatusSchema = z.enum(PROJECT_STATUSES);
export type ProjectStatus = z.infer<typeof ProjectStatusSchema>;

export const OFFICE_SLUGS = ['rancho', 'livermore', 'sparks'] as const;
export const OfficeSlugSchema = z.enum(OFFICE_SLUGS);
export type OfficeSlug = z.infer<typeof OfficeSlugSchema>;

export const PaginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type Pagination = z.infer<typeof PaginationSchema>;

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    hasMore: z.boolean(),
  });
